/**
 * KPI & Erken Uyarı Portalı — Prompt 4 iter79.
 *
 * Yönetici için sade tek-ekran risk özet portalı. Mevcut Dashboard'a
 * dokunulmaz. Her kart mevcut modülün filtreli sayfasına yönlendirir.
 *
 * KURAL: uzun açıklamalar, tablolar, gereksiz KPI'lar YOK. Sayı + kısa
 * başlık + link. Renkler: yeşil/sarı/turuncu/kırmızı.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/UI";
import {
  Factory, ShieldCheck, ShoppingCart, Boxes, Truck, ClipboardList,
  FlaskConical, BarChart3, ShieldAlert, Warehouse, Sparkles, RefreshCcw,
} from "lucide-react";

const SECTION_ICONS = {
  "ÜRETİM": Factory,
  "KALİTE KONTROL": ShieldCheck,
  "SATIN ALMA": ShoppingCart,
  "ENVANTER": Boxes,
  "SEVKİYAT": Truck,
  "SATIŞ SİPARİŞLERİ": ClipboardList,
  "AR-GE": FlaskConical,
  "FİNANS": BarChart3,
  "GERİ ÇAĞIRMA & İADE": ShieldAlert,
  "TEDARİKÇİLER": Warehouse,
  "SİSTEM SAĞLIĞI": Sparkles,
};

const LEVEL_STYLES = {
  ok:        { badge: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300", num: "text-emerald-700 dark:text-emerald-300", dot: "bg-emerald-500" },
  warn:      { badge: "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-300", num: "text-amber-700 dark:text-amber-300", dot: "bg-amber-500" },
  attention: { badge: "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/30 text-orange-700 dark:text-orange-300", num: "text-orange-700 dark:text-orange-300", dot: "bg-orange-500" },
  critical:  { badge: "bg-rose-50 dark:bg-rose-500/10 border-rose-300 dark:border-rose-500/50 text-rose-700 dark:text-rose-300", num: "text-rose-700 dark:text-rose-300", dot: "bg-rose-500" },
  inactive:  { badge: "bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400", num: "text-slate-500 dark:text-slate-400", dot: "bg-slate-400" },
};

export default function KpiPortal() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.get("/kpi-portal/summary");
      setData(r);
    } catch (e) {
      setError(e.response?.data?.detail || "Portal yüklenemedi");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const goto = (link) => { if (link) navigate(link); };

  return (
    <div>
      <PageHeader
        title="Yönetim Paneli"
        description="Operasyonel erken uyarı ve aksiyon panosu"
        actions={
          <button
            onClick={load}
            disabled={busy}
            data-testid="kpi-refresh"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 text-slate-600 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-700">
            <RefreshCcw size={12} className={busy ? "animate-spin" : ""}/> Yenile
          </button>
        }
      />

      {error && (
        <div className="mb-4 rounded-md bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      )}

      {!data && busy && (
        <div className="text-center py-16 text-slate-400 dark:text-slate-500 text-sm" data-testid="kpi-loading">Yükleniyor...</div>
      )}

      {data && (
        <div className="space-y-6" data-testid="kpi-portal-body">
          {data.sections.map((sec, i) => {
            const Icon = SECTION_ICONS[sec.title] || BarChart3;
            return (
              <div key={i} data-testid={`kpi-section-${i}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={16} className="text-indigo-500 dark:text-indigo-400"/>
                  <h2 className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {sec.title}
                  </h2>
                  <div className="flex-1 border-b border-slate-200/60 dark:border-slate-700/50 ml-2"/>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {sec.cards.map((card, j) => (
                    <KpiCard key={j} card={card} onClick={() => goto(card.link)} testid={`kpi-card-${i}-${j}`}/>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="text-[10px] text-slate-400 dark:text-slate-500 pt-2 text-right">
            Son güncelleme: {new Date(data.generated_at).toLocaleString("tr-TR")}
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ card, onClick, testid }) {
  const style = LEVEL_STYLES[card.level] || LEVEL_STYLES.ok;
  const clickable = !!card.link;
  return (
    <button
      onClick={clickable ? onClick : undefined}
      data-testid={testid}
      disabled={!clickable}
      className={`text-left rounded-lg border px-3 py-2.5 transition-all ${style.badge} ${
        clickable ? "hover:shadow-md hover:scale-[1.02] cursor-pointer" : "cursor-default opacity-90"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`}/>
        <span className="text-[11px] font-medium uppercase tracking-wide flex-1 truncate">
          {card.label}
        </span>
      </div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${style.num}`} style={{ fontFamily: "'Manrope', sans-serif" }}>
        {card.count}
      </div>
      {card.note && (
        <div className="mt-0.5 text-[10px] text-slate-500 dark:text-slate-400 truncate" title={card.note}>
          {card.note}
        </div>
      )}
    </button>
  );
}
